import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { NODEBOT_NODE_TYPES } from "../../config/urls";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import typography from "../../cssUtils/typography";
import { formatTitle, nodeColors } from "./Components/utils";

const groupByCategory = (nodeTypes) => {
  return nodeTypes.reduce((acc, nodeType) => {
    const category = nodeType.category || "other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(nodeType);
    return acc;
  }, {});
};

const Nodes = () => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [nodeTypes, setNodeTypes] = useState([]);
  const [selectedNodeType, setSelectedNodeType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const groupedNodeTypes = React.useMemo(
    () => groupByCategory(nodeTypes),
    [nodeTypes],
  );

  const fetchNodeTypes = async () => {
    const token = await getValidToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    setLoading(true);

    try {
      const response = await fetch(`${NODEBOT_NODE_TYPES}?limit=999`, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch node types");
      }

      const data = (await response.json()).results;

      setNodeTypes(data);
    } catch (error) {
      setError(error.message || "Failed to fetch node types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNodeTypes();
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          height: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "red",
        }}
      >
        <Typography sx={{ ...typography.bodyLarge, color: "red" }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="primary" size={42} />
      </Box>
    );
  }

  return nodeTypes.length > 0 ? (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        justifyItems: "flex-start",
        gap: 2,
        p: 2,
      }}
    >
      {Object.entries(groupedNodeTypes).map(([category, nodes]) => (
        <Box key={category} sx={{ width: "100%" }}>
          {/* Category Header */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography sx={{ ...typography.h2, fontWeight: 600 }}>
              {formatTitle(category)}
            </Typography>
            <Chip
              label={`${nodes.length}`}
              size="small"
              sx={{
                backgroundColor: nodeColors[category],
                color: "#fff",
                width: 26,
                height: 26,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ...typography.bodySmall,
              }}
            />
          </Stack>

          {/* Category Nodes */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mb: 4,
            }}
          >
            {nodes.map((nodeType) => (
              <NodeType
                key={nodeType.key}
                nodeType={nodeType}
                onSelect={setSelectedNodeType}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* Selected Node Modal */}
      <NodeTypeDialog
        nodeType={selectedNodeType}
        onClose={() => setSelectedNodeType(null)}
      />
    </Box>
  ) : (
    <Box
      sx={{
        height: 400,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography sx={{ ...typography.bodyLarge }}>
        No nodes found...
      </Typography>
    </Box>
  );
};

export default Nodes;

const NodeType = ({ nodeType, onSelect }) => {
  const { name, category, description } = nodeType;

  return (
    <Card
      variant="outlined"
      sx={{
        width: 450,
        borderRadius: 2,
        cursor: "pointer",
        transition: "0.2s",
        "&:hover": {
          boxShadow: 3,
          borderColor: "primary.main",
        },
      }}
      onClick={() => onSelect(nodeType)}
    >
      <CardContent>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ ...typography.h2, fontWeight: 600 }}>
              {formatTitle(name)}
            </Typography>
            <Chip
              label={formatTitle(category)}
              size="small"
              sx={{
                backgroundColor: nodeColors[category],
                color: "#fff",
                ...typography.bodySmall,
              }}
            />
          </Stack>

          {/* Description */}
          <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
            {description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const NodeTypeDialog = ({ nodeType, onClose }) => {
  if (!nodeType) return null;

  const { name, category, description, when_to_use, config_schema } = nodeType;

  return (
    <Dialog open={Boolean(nodeType)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ ...typography.h2, fontWeight: 600 }}>
            {formatTitle(name)}
          </Typography>
          <Chip
            label={formatTitle(category)}
            size="small"
            sx={{
              backgroundColor: nodeColors[category],
              color: "#fff",
              ...typography.bodySmall,
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography sx={{ ...typography.bodyLarge }}>
            {description}
          </Typography>

          <Divider />

          <Typography sx={{ ...typography.bodyLarge }}>
            {when_to_use}
          </Typography>

          <Divider />

          {config_schema ? (
            <Stack spacing={2}>
              {Object.entries(config_schema.properties || {}).map(
                ([field, schema]) => (
                  <Stack
                    key={field}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ flexWrap: "wrap", gap: 1 }}
                  >
                    <Stack sx={{ flex: 1 }}>
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                        {formatTitle(field)}
                        <Typography
                          component="span"
                          sx={{ ...typography.bodySmall, ml: 0.5, color: "#6C7C93" }}
                        >
                          ({schema.type})
                        </Typography>
                      </Typography>
                      {schema.description && (
                        <Typography
                          sx={{ ...typography.bodySmall, color: "text.secondary" }}
                        >
                          {schema.description}
                        </Typography>
                      )}
                      {schema.default !== undefined && (
                        <Typography
                          sx={{ ...typography.bodySmall, color: "text.secondary" }}
                        >
                          Default: {String(schema.default)}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      {schema.readOnly && (
                        <Chip
                          label="Read Only"
                          size="small"
                          color="info"
                          sx={{ ...typography.bodySmall }}
                        />
                      )}
                      {config_schema.required?.includes(field) && (
                        <Chip
                          label="Required"
                          size="small"
                          color="error"
                          sx={{ ...typography.bodySmall }}
                        />
                      )}
                    </Stack>
                  </Stack>
                ),
              )}
            </Stack>
          ) : (
            <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
              This node does not require configuration.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};